import { Label, defineLabels } from './types';

/**
 * The shared map: one Spanish name per DB column, for columns that mean the
 * same thing everywhere.
 *
 * Most of them do — `notes`, `date`, `subtotal`, `tax`, `kilos`, `groups`. A map
 * per entity would hold fourteen copies of "Notas" free to drift apart, which is
 * the problem this file exists to remove. Where a column genuinely means
 * something different per entity, the override goes in `ENTITY_FIELD_LABELS`.
 *
 * `scripts/check-activity-labels.cjs` in the umbrella repo cross-references
 * every key here against the Prisma schema, so it is the one map with an
 * automated completeness check. Adding a synthetic key that no column backs
 * would break that — those belong in `COLUMN_LABELS` (§10.5), a separate
 * keyspace.
 *
 * Because the raw snapshots are what get stored, adding a label here applies
 * retroactively to every activity ever recorded.
 */
export const FIELD_LABELS = defineLabels({
    order_code: { short: 'Folio', long: 'Código' },
    invoice_code: { short: 'Factura', long: 'Folio de factura' },
    // Generic on purpose. A sale's date is the invoice date and an expense's is
    // when the purchase happened, so the three entities that need to say which
    // date this is override it in ENTITY_FIELD_LABELS.
    date: { long: 'Fecha' },
    subtotal: { long: 'Subtotal' },
    tax: { long: 'IVA' },
    total_with_tax: { long: 'Total con IVA' },
    notes: { long: 'Notas' },
    canceled: { long: 'Cancelada' },
    // Neutral on purpose: `accounts` holds both clients and suppliers, so the
    // precise word comes from ENTITY_FIELD_LABELS and this is only the fallback
    // for entities that have not been given one.
    account_id: { long: 'Cuenta' },
    order_sale_status_id: { long: 'Status' },
    order_quotation_status_id: { long: 'Status' },
    // "Tipo" is enough on a form or table that is already about one sale or one
    // expense; the diff row has to say which kind of "tipo" it is.
    receipt_type_id: { short: 'Tipo', long: 'Tipo de comprobante' },
    // Also overridden per entity: a sale expects to COLLECT on this date, an
    // expense expects to PAY.
    expected_payment_date: { long: 'Fecha de pago esperada' },
    require_invoice_code: { long: 'Requiere folio' },
    require_tax: { long: 'Requiere IVA' },
    // Cotizaciones: vigencia + condiciones de pago (free text). Rendered by the
    // quotation form and its audit diff.
    expiration_date: { long: 'Vigencia' },
    payment_terms: { long: 'Condiciones de pago' },
    require_supplement: { long: 'Requiere complemento' },
    supplement_code: { long: 'Folio de complemento' },
    require_credit_note: { long: 'Requiere nota de crédito' },
    credit_note_code: { long: 'Folio de nota de crédito' },
    credit_note_amount: { long: 'Monto de nota de crédito' },
    kilos: { long: 'Kilos' },
    // The "$ del …" idiom is what the product tables already use, and it is the
    // only form short enough for a money column header.
    kilo_price: { short: '$ del kilo', long: 'Precio por kilo' },
    groups: { long: 'Bultos' },
    group_price: { short: '$ del bulto', long: 'Precio por bulto' },
    group_weight: { short: 'Peso', long: 'Peso por bulto' },
    discount: { long: 'Descuento' },
    product_id: { long: 'Producto' },
    // Cotizaciones free line: the specification typed when there is no product.
    proposed_description: { short: 'Especificación', long: 'Especificación' },
    // Expenses. `date`, `notes`, `subtotal`, `tax`, `total_with_tax`,
    // `canceled`, `require_tax`, `require_supplement`, `supplement_code`,
    // `expected_payment_date` and `receipt_type_id` are shared with sales above.
    // The supplier's invoice folio — the expense-side twin of `invoice_code`,
    // hence the same short.
    external_code: { short: 'Factura', long: 'Folio externo' },
    internal_code: { long: 'Folio interno' },
    tax_retained: { long: 'IVA retenido' },
    non_tax_retained: { long: 'ISR retenido' },
    require_external_code: { long: 'Requiere folio externo' },
    reconciliation_only: {
        short: 'Solo conciliación (no sumar en resúmenes)',
        long: 'Solo conciliación',
    },
    is_draft: {
        short: 'Borrador',
        long: 'Borrador',
    },
    resources_total: { long: 'Total de materiales' },
    locked: { long: 'Bloqueada' },
    units: { short: 'Cantidad', long: 'Unidades' },
    unit_price: { short: '$ unitario', long: 'Precio unitario' },
    resource_id: { long: 'Material' },
    // Transfers.
    amount: { long: 'Monto' },
    transferred: { long: 'Transferido' },
    transferred_date: { short: 'Fecha', long: 'Fecha de transferencia' },
    expected_date: { long: 'Fecha esperada' },
    transfer_type_id: { short: 'Tipo', long: 'Tipo de transferencia' },
    // Not reachable from the forms: the transfer form computes these labels
    // from the transfer type ("Retiro (cuenta del cliente)" vs "Retiro
    // (nuestra cuenta)"), which no static dictionary can express. Only the
    // audit reads them.
    from_account_id: { long: 'Cuenta origen' },
    to_account_id: { long: 'Cuenta destino' },
    order_sale_id: { long: 'Venta' },
    // "Gasto", not "Compra" — that is the word every other surface uses.
    expense_id: { long: 'Gasto' },
    // Products, machines, employees, resources and accounts.
    code: { long: 'Código' },
    description: { long: 'Descripción' },
    name: { long: 'Nombre' },
    first_name: { long: 'Nombre' },
    last_name: { long: 'Apellidos' },
    fullname: { long: 'Nombre completo' },
    email: { long: 'Correo' },
    cellphone: { long: 'Celular' },
    abbreviation: { long: 'Abreviación' },
    rfc: { long: 'RFC' },
    address: { long: 'Domicilio' },
    branch_id: { long: 'Sucursal' },
    role_id: { long: 'Rol' },
    // Checkbox labels: inside a box already headed "Configuración" the "Es "
    // prefix is noise, but a diff row reading just "Cliente" would look like a
    // reference to an account.
    is_client: { short: 'Cliente', long: 'Es cliente' },
    is_supplier: { short: 'Proveedor', long: 'Es proveedor' },
    is_own_company: { long: 'Es empresa propia' },
    product_category_id: { long: 'Categoría' },
    resource_category_id: { long: 'Categoría' },
    machine_id: { long: 'Máquina' },
    employee_id: { long: 'Empleado' },
    order_request_status_id: { long: 'Status' },
    order_quotation_id: { long: 'Cotización' },
    account_products_updated_at: { long: 'Catálogo actualizado' },
    order_request_completed_at: { long: 'Pedido completado' },
    estimated_delivery_date: { long: 'Fecha estimada de entrega' },
    start_date: { short: 'Fecha', long: 'Fecha de inicio' },
    waste: { long: 'Desperdicio' },
    hours: { long: 'Horas' },
    is_leader: { short: 'Operador', long: 'Es operador' },
    // The "actual" is what distinguishes these from the per-row prices in a
    // diff. On the product and resource forms the whole section is the current
    // prices, so the word is already on screen.
    current_unit_price: { short: '$ unitario', long: 'Precio unitario actual' },
    current_group_price: {
        short: '$ del bulto',
        long: 'Precio por bulto actual',
    },
    current_group_weight: { short: 'Peso', long: 'Peso por bulto actual' },
    // ── Gap sweep, 2026-07-31 (feature doc §9.4) ─────────────────────────────
    // Every remaining scalar column of every snapshotted table, swept against
    // the Prisma schema. Before this, ~59 columns fell through to humanize()
    // and rendered as raw English column names inside a Spanish table —
    // `automatic_tax_calculation` being the one that prompted it.
    //
    // Deliberately absent: `password` and `remember_token`. They exist on
    // `users` but `getUserSnapshot` selects explicit safe columns, so they can
    // never reach a diff. A label for them would imply they are expected here.
    created_by_id: { long: 'Creado por' },
    // ── Added by phase 2 (§10.8), for the paginated tables ───────────────────
    // Neither is snapshotted, so `check-activity-labels.cjs` never asked for
    // them — but `updated_by_id` is a real column on 33 tables and §10.5 is
    // explicit that "Actualizado por" is a FIELD_LABELS entry, not a composite.
    // Putting it here is what makes the "Actualizada por" / "Actualizado por"
    // split fixable in one place.
    updated_by_id: { long: 'Actualizado por' },
    created_at: { short: 'Fecha', long: 'Fecha de creación' },
    updated_at: { long: 'Última actualización' },
    // Order sales.
    automatic_tax_calculation: { long: 'Cálculo de IVA automático' },
    order_request_id: { long: 'Pedido' },
    transfer_receipts_total: { long: 'Total aplicado' },
    transfer_receipts_total_no_adjustments: {
        long: 'Total aplicado (sin ajustes)',
    },
    // Expenses and transfers.
    generated_from_expense_id: { long: 'Generado desde gasto' },
    transfer_id: { long: 'Transferencia' },
    // Order requests, adjustments and productions.
    priority: { long: 'Prioridad' },
    order_adjustment_id: { long: 'Ajuste' },
    order_adjustment_type_id: { short: 'Tipo', long: 'Tipo de ajuste' },
    order_production_id: { long: 'Orden de producción' },
    order_production_type_id: { long: 'Tipo de producción' },
    shift: { long: 'Turno' },
    // Products and resources.
    external_description: { long: 'Descripción externa' },
    internal_description: { long: 'Descripción interna' },
    current_kilo_price: { short: '$ del kilo', long: 'Precio por kilo actual' },
    width: { long: 'Ancho' },
    length: { long: 'Largo' },
    pleat: { long: 'Fuelle' },
    calibre: { long: 'Calibre' },
    discontinued: { long: 'Descontinuado' },
    group_weight_strict: { long: 'Peso por bulto estricto' },
    include_units_in_summary: {
        short: 'Incluir kilos/bultos en resúmenes',
        long: 'Incluir unidades en resumen',
    },
    product_material_id: { short: 'Material', long: 'Material del producto' },
    unit_price_name: { long: 'Nombre del precio unitario' },
    // Machines.
    machine_type_id: { long: 'Tipo de máquina' },
    consumes_input: { long: 'Consume material de entrada' },
    // Employees.
    base_salary: { long: 'Sueldo base' },
    // The parenthetical is a data-entry hint — it belongs next to the input,
    // not in a diff row.
    hours_should_work: {
        short: 'Horas base (48 / 52.5)',
        long: 'Horas a trabajar',
    },
    employee_category_id: { long: 'Categoría de empleado' },
    employee_type_id: { long: 'Tipo de empleado' },
    is_inactive: { long: 'Dado de baja' },
    credit: { long: 'Crédito' },
    credit_required: { long: 'Requiere crédito' },
    infonavit: { long: 'Infonavit' },
    // Users.
    user_id: { long: 'Usuario' },
    // Accounts — the widest table, and the one where an unlabelled column is
    // most confusing because client_* and supplier_* differ by one word.
    house_phone: { long: 'Teléfono' },
    address1: { long: 'Dirección 1' },
    address2: { long: 'Dirección 2' },
    city: { long: 'Ciudad' },
    country: { long: 'País' },
    zip_code: { long: 'Código postal' },
    is_own: { long: 'Es cuenta propia' },
    requires_order_request: { long: 'Requiere pedido' },
    monitor_supplier_expenses: {
        short: 'Monitorear gastos',
        long: 'Monitorear gastos del proveedor',
    },
    merged_into_account_id: { long: 'Fusionada con la cuenta' },
    is_informal_account: {
        // Read-only on the form, and only ever true for "Inopack Notas" — the
        // informal side of the money (efectivo). The accounting-export exclusion
        // is a derivation of this.
        short: 'Cuenta informal (notas / efectivo)',
        long: 'Cuenta informal — dinero no formal (efectivo)',
    },
    exclude_from_balances_summary: {
        // Read-only on the form, seeded on Notas + Efectivo. Removes the account
        // from the Saldos y Pagos / Transfers ledger and the Acumulado (cash is
        // not reliably tracked). Independent of is_informal_account.
        short: 'Excluir de saldos y pagos',
        long: 'Excluir de saldos y pagos (no aparece ni suma al acumulado)',
    },
    // ── The pairs that motivated the two variants in the first place ─────────
    // Each of these lives inside a form tab already titled *Cliente* or
    // *Proveedor*, where repeating the word stutters. The diff lists twenty
    // fields of mixed provenance in one unheaded column, where dropping it
    // would make the two halves indistinguishable.
    client_credit_days: {
        short: 'Días de crédito',
        long: 'Días de crédito (cliente)',
    },
    client_require_credit_note: {
        short: 'Requiere nota de crédito',
        long: 'Cliente requiere nota de crédito',
    },
    client_require_supplement: {
        short: 'Requiere complemento',
        long: 'Cliente requiere complemento',
    },
    client_requires_invoice_code: {
        short: 'Requiere folio en facturas gravadas',
        long: 'Cliente requiere folio de factura',
    },
    client_requires_tax: {
        short: 'Requiere IVA en facturas gravadas',
        long: 'Cliente requiere IVA',
    },
    client_automatic_tax_calculation: {
        short: 'Cálculo de IVA automático',
        long: 'Cliente: cálculo de IVA automático',
    },
    client_reconciliation_only: {
        short: 'Solo conciliación por defecto (ventas)',
        long: 'Cliente: ventas nuevas como solo conciliación',
    },
    supplier_reconciliation_only: {
        short: 'Solo conciliación por defecto (gastos)',
        long: 'Proveedor: gastos nuevos como solo conciliación',
    },
    supplier_is_draft: {
        short: 'Borrador por defecto',
        long: 'Proveedor: gastos nuevos como borrador',
    },
    supplier_credit_days: {
        short: 'Días de crédito',
        long: 'Días de crédito (proveedor)',
    },
    supplier_require_supplement: {
        short: 'Requiere complemento',
        long: 'Proveedor requiere complemento',
    },
    supplier_requires_external_code: {
        short: 'Requiere folio en recibos gravados',
        long: 'Proveedor requiere folio externo',
    },
    supplier_requires_tax: {
        short: 'Requiere IVA en recibos gravados',
        long: 'Proveedor requiere IVA',
    },
    supplier_recurring_expenses: {
        short: 'Gastos recurrentes',
        long: 'Gastos recurrentes del proveedor',
    },
});

/** Every column the dictionary names. Forms and tables should key off this. */
export type FieldLabelKey = keyof typeof FIELD_LABELS;

/**
 * Dynamic lookup, for the audit only.
 *
 * The diff walks column names out of a stored snapshot, so it cannot know them
 * at compile time and must be able to ask for a column that has no entry — that
 * is what makes an unlabelled column show up as raw English rather than vanish.
 * Forms and tables have the opposite requirement and should index `FIELD_LABELS`
 * directly, so that a missing key fails the build.
 */
export function lookupFieldLabel(column: string): Label | undefined {
    return (FIELD_LABELS as Record<string, Label | undefined>)[column];
}
